import { Search, Filter, X } from 'lucide-react';
import type { Solicitante, Pessoa, Area, Prioridade, TipoEntrega } from '@/lib/types';

export type PrazoFilter = '' | 'atrasado' | 'hoje' | 'semana' | 'sem-prazo';

// Filtros enxutos (decisão Renan 08/07): cliente · produto · time · evento · prazo · executor.
// Removidos da UI: A/B (redundante com evento), categoria do solicitante (redundante com origem).
export interface FilterState {
  search: string;
  solicitanteId: string;
  prioridade: Prioridade | '';
  origemTipo: 'Externo (contratual)' | 'Apex (UN)' | 'Research (interno)' | '';
  tipoProduto: string;
  tipoEntrega: TipoEntrega | '';
  executorId: string;
  areaId: string;
  prazo: PrazoFilter;
  eventoId: string;
}

interface Props {
  state: FilterState;
  onChange: (next: FilterState) => void;
  solicitantes: Solicitante[];
  pessoas?: Pessoa[];
  areas?: Area[];
  tiposProduto?: string[];
  eventos?: { id: string; nome: string }[];
  totalDemandas: number;
  filteredCount: number;
  searchRef?: React.RefObject<HTMLInputElement>;
}

export function KanbanFilters({
  state, onChange, solicitantes, pessoas = [], areas = [], tiposProduto = [], eventos = [], totalDemandas, filteredCount, searchRef,
}: Props) {
  const hasFilters =
    state.search || state.solicitanteId || state.prioridade ||
    state.origemTipo || state.tipoProduto || state.tipoEntrega ||
    state.executorId || state.areaId || state.eventoId || state.prazo;

  function clear() {
    onChange({
      search: '', solicitanteId: '', prioridade: '',
      origemTipo: '', tipoProduto: '', tipoEntrega: '',
      executorId: '', areaId: '', eventoId: '', prazo: '',
    });
  }

  const executores = pessoas
    .filter(p => (p.papel === 'Executor' || p.papel === 'Coordenação') && p.ativo)
    .sort((a, b) => a.nome.localeCompare(b.nome));

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <div className="relative flex-1 min-w-[220px] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
        <input
          ref={searchRef}
          className="input w-full pl-9"
          placeholder="Buscar por título, pauta..."
          value={state.search}
          onChange={(e) => onChange({ ...state, search: e.target.value })}
        />
      </div>

      <select
        className="input"
        value={state.solicitanteId}
        onChange={(e) => onChange({ ...state, solicitanteId: e.target.value })}
      >
        <option value="">Todos solicitantes</option>
        {solicitantes
          .filter((s) => s.status === 'Ativo')
          .sort((a, b) => a.nome.localeCompare(b.nome))
          .map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
      </select>

      {tiposProduto.length > 0 && (
        <select
          className="input"
          value={state.tipoProduto}
          onChange={(e) => onChange({ ...state, tipoProduto: e.target.value })}
          title="Tipo de produto"
        >
          <option value="">Todo produto</option>
          {tiposProduto.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      )}

      <select
        className="input"
        value={state.prioridade}
        onChange={(e) => onChange({ ...state, prioridade: e.target.value as Prioridade | '' })}
      >
        <option value="">Toda prioridade</option>
        <option value="Crítica">Crítica</option>
        <option value="Alta">Alta</option>
        <option value="Média">Média</option>
        <option value="Baixa">Baixa</option>
      </select>

      <select
        className="input"
        value={state.origemTipo}
        onChange={(e) => onChange({ ...state, origemTipo: e.target.value as FilterState['origemTipo'] })}
      >
        <option value="">Toda origem</option>
        <option value="Externo (contratual)">Cliente</option>
        <option value="Apex (UN)">Apex (UN)</option>
        <option value="Research (interno)">Research interno</option>
      </select>

      <select
        className="input"
        value={state.tipoEntrega}
        onChange={(e) => onChange({ ...state, tipoEntrega: e.target.value as TipoEntrega | '' })}
        title="Tipo de entrega"
      >
        <option value="">Todo tipo</option>
        <option value="producao">Produção</option>
        <option value="checklist">Checklist</option>
        <option value="comercial">Comercial</option>
      </select>

      {executores.length > 0 && (
        <select
          className="input"
          value={state.executorId}
          onChange={(e) => onChange({ ...state, executorId: e.target.value })}
          title="Executor"
        >
          <option value="">Todo executor</option>
          {executores.map(p => <option key={p.id} value={p.id}>{p.nome.split(' ')[0]}</option>)}
        </select>
      )}

      {areas.length > 0 && (
        <select
          className="input"
          value={state.areaId}
          onChange={(e) => onChange({ ...state, areaId: e.target.value })}
          title="Área"
        >
          <option value="">Toda área</option>
          {areas.sort((a, b) => a.nome.localeCompare(b.nome)).map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
        </select>
      )}

      {eventos.length > 0 && (
        <select
          className="input"
          value={state.eventoId}
          onChange={(e) => onChange({ ...state, eventoId: e.target.value })}
          title="Evento"
        >
          <option value="">Todo evento</option>
          {eventos.map((ev) => <option key={ev.id} value={ev.id}>{ev.nome}</option>)}
        </select>
      )}

      <select
        className="input"
        value={state.prazo}
        onChange={(e) => onChange({ ...state, prazo: e.target.value as PrazoFilter })}
        title="Prazo SLA"
      >
        <option value="">Todo prazo</option>
        <option value="atrasado">⚠️ Atrasado</option>
        <option value="hoje">Vence hoje</option>
        <option value="semana">Vence esta semana</option>
        <option value="sem-prazo">Sem prazo</option>
      </select>

      {hasFilters && (
        <button
          type="button" onClick={clear}
          className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-muted hover:text-text"
        >
          <X className="w-3 h-3" /> limpar
        </button>
      )}

      <div className="text-xs text-muted ml-auto">
        <Filter className="w-3 h-3 inline mr-1" />
        {filteredCount} de {totalDemandas} demandas
      </div>
    </div>
  );
}
